/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo } from 'react';
import type { Feed, FeedEntry, UIComponent } from '~/services/layout/index';
import { twMerge } from 'tailwind-merge';
import { MaybeLink } from '~/components/maybe-link';
import { ResponsiveImage } from './responsive-image';
import logger from '~/services/logger';
import { RuntimeV2 } from './runtime-v2';
import {
  type TapCellAnalyticsData,
  useTapCellAnalyticsData,
} from '~/hooks/use-tap-cell-analytics-data';
import { ProgressBarV2 } from './progress-bar-v2';
import { ActionIconV2 } from './action-icon-v2';
import {
  getCellClasses,
  getCellTextLabelClasses,
  getCellTextLabelsV2,
  getCellEntryImgSources,
  getCellBreakpointValues,
  getCellContainerCellTypeClasses,
  getCellImageCellTypeClasses,
  getCell3TextLabelContainerClasses,
  getCellHiddenClasses,
  getCellImagePlaceholderColorClasses,
  getCellImageAspectRatioClasses,
  cellTextLabels,
  getResponsiveCellValues,
  getPointerEventsClasses,
  type CellTextLabelsV2,
  type ResponsiveSources,
  getCellImageContainerClasses,
  getCell3IgnorePaddingClasses,
  getCellImageHiddenClasses,
  shouldRenderPowerCellImage,
} from './power-cell-getters';
import { useLoaderData } from '@remix-run/react';
import type { loader as rootLoader } from '~/routes/_index.ts';
import { getResponsiveCellConfigs } from './get-responsive-cell-configs';
import {
  getContinueWatchingProgress,
  isEmptyArray,
  seoSelectedBreakpoint,
} from '~/services/layout/layout';
import { LockBadge } from './lock-badge';
import { LabelWrapper } from './label-wrapper';
import { useTrackAnalyticsData } from '~/hooks/use-track-analytics-data';
import { PowerCellBorder } from './power-cell-border';
import {
  useResponsiveBreakpoint,
  type UseResponsiveBreakpoint,
} from '~/hooks/use-responsive-breakpoint';
import { InfoCellV2 } from './info-cell-v2';
import { CellBadge } from './cell-badge';
import { ContentBadge } from './content-badge';
import { usePreferenceSelector } from '~/hooks/preference-selector/use-preference-selector';
import {
  useFavoriteAction,
  type UseFavoriteActionReturnType,
} from '~/hooks/use-favorite-action';
import type { Action } from '~/hooks/actions/types';
import type { StorageFeedsData } from '~/components/ui-component';
import type { UseIsLoggedInReturnType } from '~/hooks/use-is-logged-in';
import {
  type AllDynamicColorsReturnType,
  getAllDynamicColors,
} from './get-cell-dynamic-colors';

export function PowerCellComponent({
  uiComponent,
  entry,
  selectable,
  cellClassNames,
  cellRef,
  feedRootWebLink,
  isLoading,
  storageFeedsData,
  isLoggedInData,
}: {
  uiComponent: UIComponent;
  entry: FeedEntry;
  selectable?: boolean;
  cellClassNames?: string;
  cellRef?: any;
  feedRootWebLink?: string;
  isLoading?: boolean;
  storageFeedsData?: StorageFeedsData;
  isLoggedInData?: UseIsLoggedInReturnType | undefined;
}): JSX.Element {
  const { cellStyles, timeZoneHeader, linkPrefetchSwitch } =
    useLoaderData<typeof rootLoader>();

  const progress: number | undefined = getContinueWatchingProgress({
    entry,
    continueWatchingFeed: storageFeedsData?.['continue-watching']?.feed,
  });

  isLoading = useMemo(
    () => isLoading || entry?._isLoadingSkeletonEntry,
    [entry?._isLoadingSkeletonEntry, isLoading]
  );

  const tapCellAnalyticsData: TapCellAnalyticsData = useTapCellAnalyticsData({
    uiComponent,
    entry,
  });

  const trackAnalyticsData: Function =
    useTrackAnalyticsData(tapCellAnalyticsData);

  const responsiveBreakpoint: UseResponsiveBreakpoint =
    useResponsiveBreakpoint();

  const {
    isPreferenceSelectorCellAndUnselected,
    isPreferenceSelectorCellAndSelected,
    preferenceSelectorActionFunction,
  } = usePreferenceSelector(entry, uiComponent);

  const favoritesFeed: Feed | undefined = storageFeedsData?.favorites?.feed;

  const favoriteActionData: UseFavoriteActionReturnType = useFavoriteAction({
    entry,
    favoritesFeed,
    isLoggedInData,
  });

  const tapActions = entry?.extensions?.tap_actions?.actions;

  const hasTapActions: boolean = useMemo(
    () => !isEmptyArray(tapActions as Action[]),
    [tapActions]
  );

  const handleClick = useCallback(() => {
    if (isPreferenceSelectorCellAndSelected && !hasTapActions) return;
    preferenceSelectorActionFunction();
  }, [
    hasTapActions,
    isPreferenceSelectorCellAndSelected,
    preferenceSelectorActionFunction,
  ]);

  const entryWebLink: string = useMemo(
    () => (isPreferenceSelectorCellAndUnselected ? '' : entry?._webLink || ''),
    [entry?._webLink, isPreferenceSelectorCellAndUnselected]
  );

  const isPowerCellHover: boolean = useMemo(
    () => !!(entryWebLink || isPreferenceSelectorCellAndUnselected),
    [entryWebLink, isPreferenceSelectorCellAndUnselected]
  );

  const {
    identifiers,
    styles,
    assets,
    cellClasses,
    textLabelClasses,
    textLabelsAlignment,
    pointerEventsClasses,
    powerCellClasses,
    cellContainerCellTypeClasses,
    cellImageContainerClasses,
    cellImageClasses,
    cellLabelContainerClasses,
  } = usePowerCellConfig({
    cellStyles,
    uiComponent,
    isPreferenceSelectorCellAndUnselected,
    isPreferenceSelectorCellAndSelected,
    isPowerCellHover,
    cellClassNames,
  });

  const defaultStyles = styles[seoSelectedBreakpoint];

  const textLabels: CellTextLabelsV2 = useMemo(
    () =>
      getCellTextLabelsV2({
        entry,
        styles,
        identifiers,
        timeZoneHeader,
        isLoading,
      }),
    [entry, identifiers, isLoading, styles, timeZoneHeader]
  );

  const overlayImageSources: ResponsiveSources = useMemo(
    () =>
      getCellBreakpointValues({
        config: assets,
        key: 'image_overlay',
        prevBreakpointFallback: false,
      }),
    [assets]
  );

  const cellImageSources: ResponsiveSources = useMemo(
    () =>
      getCellEntryImgSources({
        assets,
        entry,
      }),
    [assets, entry]
  );

  const dynamicColors = useMemo(
    () =>
      getAllDynamicColors({
        uiComponent,
        entry,
        cellStyles,
      }),
    [uiComponent, entry, cellStyles]
  ) as AllDynamicColorsReturnType;

  const LabelWrapperDynamicColors = useMemo(() => {
    return {
      textLabel: dynamicColors.textLabel,
      buttonsContainer: dynamicColors.buttonsContainer,
    };
  }, [dynamicColors.textLabel, dynamicColors.buttonsContainer]);

  try {
    return (
      <div
        className={twMerge(pointerEventsClasses, 'h-full')}
        data-testid={`power-cell-${entry?.id}-${uiComponent?.id}`}
        onClick={handleClick}
      >
        <div
          ref={cellRef}
          className={powerCellClasses}
          css={dynamicColors.cell}
        >
          <MaybeLink
            to={entryWebLink}
            enabled={selectable}
            linkariainfo={entry?.title}
            analyticsdata={tapCellAnalyticsData}
            linkPrefetchSwitch={linkPrefetchSwitch}
          >
            <PowerCellBorder
              isPowerCellBorderHover={isPowerCellHover}
              dynamicColors={dynamicColors.cellBorder}
            />

            <InfoCellV2
              identifiers={identifiers}
              assets={assets}
              styles={styles}
              textLabels={textLabels}
              cellClasses={cellClasses}
              feedRootWebLink={feedRootWebLink}
              trackAnalyticsData={trackAnalyticsData}
              defaultStyles={defaultStyles}
              entry={entry}
              isLoading={isLoading}
              dynamicColors={dynamicColors.infoCell}
            />
            <div className={cellContainerCellTypeClasses}>
              <div
                className={`relative rounded-image-border-radius bg-transparent ${cellImageContainerClasses}`}
              >
                <LockBadge entry={entry} assets={assets} />

                <RuntimeV2
                  entry={entry}
                  styles={styles}
                  dynamicColors={dynamicColors.runtime}
                />

                {!isLoading && (
                  <ActionIconV2
                    assets={assets}
                    entry={entry}
                    favoriteActionData={favoriteActionData}
                  />
                )}

                <ContentBadge entry={entry} assets={assets} />

                {shouldRenderPowerCellImage({ assets, identifiers }) && (
                  <div className={cellImageClasses}>
                    <ResponsiveImage
                      imageSources={overlayImageSources}
                      altText={entry?.title && `${entry?.title} overlay`}
                      classNames="z-[3] absolute aspect-cell-image w-full object-cover"
                    />
                    <CellImage
                      entry={entry}
                      cellClasses={cellClasses}
                      isPowerCellImageHover={isPowerCellHover}
                      assets={assets}
                      identifiers={identifiers}
                      imageSources={cellImageSources}
                      isLoading={isLoading}
                      dynamicColors={dynamicColors}
                    />
                  </div>
                )}

                <ProgressBarV2
                  assets={assets}
                  progress={progress}
                  dynamicColors={dynamicColors.progressBar}
                />
              </div>

              {/* Cell 1 and 2 Labels */}
              <div className="z-[6] flex flex-1 flex-col justify-center break-words">
                <LabelWrapper
                  textLabelNames={[
                    'text_label_1',
                    'text_label_2',
                    'text_label_3',
                    'text_label_4',
                  ]}
                  assets={assets}
                  textLabels={textLabels}
                  textLabelsAlignment={textLabelsAlignment}
                  textLabelClasses={textLabelClasses}
                  defaultStyles={defaultStyles}
                  entryWebLink={entryWebLink}
                  trackAnalyticsData={trackAnalyticsData}
                  identifiers={identifiers}
                  responsiveBreakpoint={responsiveBreakpoint}
                  entry={entry}
                  isLoading={isLoading}
                  isLabelHover={isPowerCellHover}
                  isPreferenceSelectorCellAndSelected={
                    isPreferenceSelectorCellAndSelected
                  }
                  favoriteActionData={favoriteActionData}
                  dynamicColors={LabelWrapperDynamicColors}
                />
              </div>
              {/* End Cell 1 and 2 Labels */}

              <CellBadge assets={assets} isHoverBadge={isPowerCellHover} />

              {/* Cell 3 Labels */}
              <div className={cellLabelContainerClasses.top}>
                <LabelWrapper
                  textLabelNames={[
                    'top_text_label_1',
                    'top_text_label_2',
                    'top_text_label_3',
                  ]}
                  assets={assets}
                  textLabels={textLabels}
                  textLabelsAlignment={textLabelsAlignment}
                  textLabelClasses={textLabelClasses}
                  defaultStyles={defaultStyles}
                  entryWebLink={entryWebLink}
                  trackAnalyticsData={trackAnalyticsData}
                  identifiers={identifiers}
                  responsiveBreakpoint={responsiveBreakpoint}
                  entry={entry}
                  isLoading={false}
                  isLabelHover={isPowerCellHover}
                  favoriteActionData={favoriteActionData}
                  dynamicColors={LabelWrapperDynamicColors}
                />
              </div>

              <div className={cellLabelContainerClasses.center}>
                <LabelWrapper
                  textLabelNames={[
                    'center_text_label_1',
                    'center_text_label_2',
                    'center_text_label_3',
                  ]}
                  assets={assets}
                  textLabels={textLabels}
                  textLabelsAlignment={textLabelsAlignment}
                  textLabelClasses={textLabelClasses}
                  defaultStyles={defaultStyles}
                  entryWebLink={entryWebLink}
                  trackAnalyticsData={trackAnalyticsData}
                  identifiers={identifiers}
                  responsiveBreakpoint={responsiveBreakpoint}
                  entry={entry}
                  isLoading={false}
                  isLabelHover={isPowerCellHover}
                  favoriteActionData={favoriteActionData}
                  dynamicColors={LabelWrapperDynamicColors}
                />
              </div>

              <div className={cellLabelContainerClasses.bottom}>
                <LabelWrapper
                  textLabelNames={[
                    'bottom_text_label_1',
                    'bottom_text_label_2',
                    'bottom_text_label_3',
                  ]}
                  assets={assets}
                  textLabels={textLabels}
                  textLabelsAlignment={textLabelsAlignment}
                  textLabelClasses={textLabelClasses}
                  defaultStyles={defaultStyles}
                  entryWebLink={entryWebLink}
                  trackAnalyticsData={trackAnalyticsData}
                  identifiers={identifiers}
                  responsiveBreakpoint={responsiveBreakpoint}
                  entry={entry}
                  isLoading={false}
                  isLabelHover={isPowerCellHover}
                  favoriteActionData={favoriteActionData}
                  dynamicColors={LabelWrapperDynamicColors}
                />
              </div>
              {/* End Cell 3 Labels */}
            </div>
          </MaybeLink>
        </div>
      </div>
    );
  } catch (error: any) {
    logger.info(`PowerCell: ${error.message}`);
    return <></>;
  }
}

const usePowerCellConfig = ({
  cellStyles,
  uiComponent,
  isPreferenceSelectorCellAndUnselected,
  isPreferenceSelectorCellAndSelected,
  isPowerCellHover,
  cellClassNames,
}: any) => {
  const { identifiers, styles, assets } = useMemo(
    () =>
      getResponsiveCellConfigs({
        cellStyles,
        uiComponent,
      }),
    [cellStyles, uiComponent]
  );

  const cellClasses: string = useMemo(
    () => (uiComponent ? getCellClasses(uiComponent) : ''),
    [uiComponent]
  );

  const textLabelClasses: Record<string, string> = useMemo(
    () => getCellTextLabelClasses(cellClasses),
    [cellClasses]
  );

  const textLabelsAlignment = useMemo(
    () =>
      getResponsiveCellValues({
        cellKeys: cellTextLabels,
        cellKeysNameSuffix: 'text_alignment',
        config: styles,
        identifiers,
      }),
    [identifiers, styles]
  );

  const pointerEventsClasses: string = useMemo(
    () =>
      getPointerEventsClasses({
        assets,
        identifiers,
        isPreferenceSelectorCellAndUnselected,
      }),
    [assets, identifiers, isPreferenceSelectorCellAndUnselected]
  );

  const powerCellClasses: string = useMemo(
    () =>
      twMerge(
        cellClasses,
        'h-full',
        'z-0',
        'relative shrink-0 overflow-hidden rounded-cell-border-radius bg-cell pb-cell-b pl-cell-l pr-cell-r pt-cell-t',
        isPowerCellHover && 'hover:bg-cell-hover',
        isPreferenceSelectorCellAndSelected &&
          'bg-cell-selected hover:bg-cell-selected-hover',
        'rounded-cell-radius',
        'group',
        'custom-group',

        'cell-1-mobile',
        'cell-1-tablet',
        'cell-1-desktop',
        'cell-1-large-desktop',

        'cell-2-mobile',
        'cell-2-tablet',
        'cell-2-desktop',
        'cell-2-large-desktop',

        'cell-3-mobile',
        'cell-3-tablet',
        'cell-3-desktop',
        'cell-3-large-desktop',

        'cell-info-mobile',
        'cell-info-tablet',
        'cell-info-desktop',
        'cell-info-large-desktop',

        cellClassNames
      ),
    [
      cellClasses,
      isPowerCellHover,
      isPreferenceSelectorCellAndSelected,
      cellClassNames,
    ]
  );

  const cellContainerCellTypeClasses: string = useMemo(
    () =>
      getCellContainerCellTypeClasses({
        identifiers,
      }),
    [identifiers]
  );

  const cellImageContainerClasses: string = useMemo(
    () =>
      getCellImageContainerClasses({
        identifiers,
        assets,
      }),
    [assets, identifiers]
  );

  const cellImageClasses: string = useMemo(
    () =>
      getCellImageHiddenClasses({
        identifiers,
        assets,
      }),
    [assets, identifiers]
  );

  const cellLabelContainerClasses = useMemo(
    () => ({
      top: twMerge(
        getCellHiddenClasses({
          identifiers,
          identifier: 'cell_3_v2',
          classNames: 'absolute left-0 top-0 z-[6] w-full',
        }),
        getCell3IgnorePaddingClasses(identifiers, 'top')
      ),
      center: getCell3TextLabelContainerClasses({
        identifiers,
        classNames:
          'absolute left-1/2 top-1/2 z-[6] w-full -translate-x-1/2 -translate-y-1/2 transform',
      }),
      bottom: twMerge(
        getCell3TextLabelContainerClasses({
          identifiers,
          classNames: 'absolute bottom-0 left-0 z-[6] w-full',
        }),
        getCell3IgnorePaddingClasses(identifiers, 'bottom')
      ),
    }),
    [identifiers]
  );

  return {
    identifiers,
    styles,
    assets,
    cellClasses,
    textLabelClasses,
    textLabelsAlignment,
    pointerEventsClasses,
    powerCellClasses,
    cellContainerCellTypeClasses,
    cellImageContainerClasses,
    cellImageClasses,
    cellLabelContainerClasses,
  };
};

function CellImage({
  entry,
  cellClasses,
  isPowerCellImageHover,
  assets,
  identifiers,
  imageSources,
  isLoading,
  dynamicColors,
}: {
  entry: any;
  cellClasses: any;
  isPowerCellImageHover: boolean;
  assets: any;
  identifiers: any;
  imageSources: any;
  isLoading?: boolean;
  dynamicColors: AllDynamicColorsReturnType;
}) {
  if (isLoading) {
    return (
      <div
        className={twMerge(
          getCellImageAspectRatioClasses({ assets, identifiers }),
          'animate-pulse rounded-image-radius bg-skeleton_background_color',

          'relative rounded-image-radius',
          identifiers.mobile === 'cell_2_v2' &&
            'mobile:max-tablet:group-[.cell-2-mobile]:w-image-width',
          identifiers.tablet === 'cell_2_v2' &&
            'tablet:max-desktop:group-[.cell-2-tablet]:w-image-width',
          identifiers.desktop === 'cell_2_v2' &&
            'desktop:max-large-desktop:group-[.cell-2-desktop]:w-image-width',
          identifiers['large-desktop'] === 'cell_2_v2' &&
            'large-desktop:group-[.cell-2-large-desktop]:w-image-width',

          identifiers.mobile === 'cell_2_v2' &&
            'mobile:max-tablet:group-[.cell-2-mobile]:flex',
          identifiers.tablet === 'cell_2_v2' &&
            'tablet:max-desktop:group-[.cell-2-tablet]:flex',
          identifiers.desktop === 'cell_2_v2' &&
            'desktop:max-large-desktop:group-[.cell-2-desktop]:flex',
          identifiers['large-desktop'] === 'cell_2_v2' &&
            'large-desktop:group-[.cell-2-large-desktop]:flex'
        )}
        css={dynamicColors.cellImage.placeholder}
      />
    );
  }

  return (
    <div
      data-payload-image
      className={twMerge(
        getCellImagePlaceholderColorClasses({
          assets,
          identifiers,
          imageSources,
        }),
        getCellImageAspectRatioClasses({ assets, identifiers }),
        'relative rounded-image-radius',
        identifiers.mobile === 'cell_2_v2' &&
          'mobile:max-tablet:group-[.cell-2-mobile]:w-image-width',
        identifiers.tablet === 'cell_2_v2' &&
          'tablet:max-desktop:group-[.cell-2-tablet]:w-image-width',
        identifiers.desktop === 'cell_2_v2' &&
          'desktop:max-large-desktop:group-[.cell-2-desktop]:w-image-width',
        identifiers['large-desktop'] === 'cell_2_v2' &&
          'large-desktop:group-[.cell-2-large-desktop]:w-image-width',

        identifiers.mobile === 'cell_2_v2' &&
          'mobile:max-tablet:group-[.cell-2-mobile]:flex',
        identifiers.tablet === 'cell_2_v2' &&
          'tablet:max-desktop:group-[.cell-2-tablet]:flex',
        identifiers.desktop === 'cell_2_v2' &&
          'desktop:max-large-desktop:group-[.cell-2-desktop]:flex',
        identifiers['large-desktop'] === 'cell_2_v2' &&
          'large-desktop:group-[.cell-2-large-desktop]:flex'
      )}
    >
      <div
        className={twMerge(
          identifiers.mobile === 'cell_2_v2' &&
            'mobile:max-tablet:group-[.cell-2-mobile]:flex-shrink-0',
          identifiers.tablet === 'cell_2_v2' &&
            'tablet:max-desktop:group-[.cell-2-tablet]:flex-shrink-0',
          identifiers.desktop === 'cell_2_v2' &&
            'desktop:max-large-desktop:group-[.cell-2-desktop]:flex-shrink-0',
          identifiers['large-desktop'] === 'cell_2_v2' &&
            'large-desktop:group-[.cell-2-large-desktop]:flex-shrink-0'
        )}
      >
        <ResponsiveImage
          imageSources={imageSources}
          altText={entry?.title}
          classNames={getCellImageCellTypeClasses({
            identifiers,
            cellClasses,
          })}
          powerCellImage={{ isPowerCellImageHover }}
          assets={assets}
          identifiers={identifiers}
          dynamicColors={dynamicColors.cellImage}
        />
      </div>
    </div>
  );
}

export const PowerCell = React.memo(PowerCellComponent);
